import { FC, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { undeleteAvailableCategories } from 'src/assets/static/view_checkbook/useCheckbookCategories';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import useCompany from 'src/hooks/useCompany';
import useConfirm from 'src/hooks/useConfirm';
import usePeriodView from 'src/hooks/usePeriodView';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import useYearView from 'src/hooks/useYearView';
import useUndeleteGLCodeApi from 'src/services/useUndeleteGLCodeApi';

interface UnDeleteGLCodeProps {
  selectedRows: any[];
}

const UnDeleteGLCode: FC<UnDeleteGLCodeProps> = ({ selectedRows }) => {
  const {
    t,
    isPeriodView,
    isEditMode,
    setGlCodePeriodBudget,
    setGlCodesBudgetByYear,
    setEditingGlCodePeriodBudget,
    setEditingGlCodeYearBudget,
    setOriginalGlCodePeriodBudget,
    setOriginalGlCodeYearBudget,
    refreshGLCodePeriodTable,
  } = useViewCheckbook();

  const { dropdownPeriodFilter } = usePeriodView();
  const { yearViewDropdownFilter } = useYearView();
  const {
    company: { companyId },
  } = useCompany();
  const { confirm } = useConfirm();
  const { undeleteGLCode } = useUndeleteGLCodeApi(companyId);

  const isUndeleteEligibleRecords = useMemo(
    () => selectedRows.every((item) => item.isDeleted),
    [selectedRows]
  );

  const isUndeleteDisable = useMemo(
    () => selectedRows.length === 0 || !isUndeleteEligibleRecords,
    [isUndeleteEligibleRecords, selectedRows.length]
  );

  const handleUndeleteGLCode = useCallback(() => {
    confirm({
      title: t('Undelete Confirmation'),
      content: (
        <div className="p-2">
          <p>{t('Are you sure you want to undelete the selected code(s)?')}</p>
          <p>{t('All GL Department assignments will be restored.')}</p>
        </div>
      ),
      confirmButtonText: 'OK',
      cancelButtonText: 'Cancel',
      cancelColor: 'info',
    }).then(async (isUnDeleteAccepted: boolean) => {
      if (isUnDeleteAccepted) {
        try {
          const ids = selectedRows.map((item) => item.id);
          const deletedGLCodes = selectedRows
            ?.filter((el) => el?.isDeleted)
            ?.map((item) => item.id);

          const newlyAddedGLCodes = selectedRows
            ?.filter((el) => el?.isNewGlCode)
            ?.map((item) => item.id);

          const res = await undeleteGLCode({ glCodes: ids });
          const glCdIds = [...res.processedIds];
          if (res?.processedIds.length) {
            toast.success('Record(s) Undeleted Successfully');
          }

          if (isEditMode) {
            if (isPeriodView) {
              setGlCodePeriodBudget((prev) => {
                const prevItems = [...prev]
                  .map((code) => ({
                    ...code,
                    isDeleted: glCdIds.includes(code.id) ? false : code.isDeleted,
                  }))
                  ?.filter((el) => !newlyAddedGLCodes?.includes(el?.id));
                return prevItems;
              });
              setEditingGlCodePeriodBudget((prev) => {
                const prevItems = [...prev]
                  ?.map((code) => ({
                    ...code,
                    isDeleted: glCdIds.includes(code.id) ? false : code.isDeleted,
                  }))
                  ?.filter((el) => ![...newlyAddedGLCodes, ...deletedGLCodes]?.includes(el?.id));
                return prevItems;
              });
              setOriginalGlCodePeriodBudget((prev) => {
                const prevItems = [...prev]
                  ?.map((code) => ({
                    ...code,
                    isDeleted: glCdIds.includes(code.id) ? false : code.isDeleted,
                  }))
                  ?.filter((el) => ![...newlyAddedGLCodes, ...deletedGLCodes]?.includes(el?.id));
                return prevItems;
              });
            } else {
              setGlCodesBudgetByYear((prev) => {
                const prevItems = [...prev]
                  .map((code) => ({
                    ...code,
                    isDeleted: glCdIds.includes(code.id) ? false : code.isDeleted,
                    isActive: true,
                  }))
                  ?.filter((el) => !newlyAddedGLCodes?.includes(el?.id));
                return prevItems;
              });
              setEditingGlCodeYearBudget((prev) => {
                const prevItems = [...prev]
                  .map((code) => ({
                    ...code,
                    isDeleted: glCdIds.includes(code.id) ? false : code.isDeleted,
                  }))
                  ?.filter((el) => ![...newlyAddedGLCodes, ...deletedGLCodes]?.includes(el?.id));
                return prevItems;
              });
              setOriginalGlCodeYearBudget((prev) => {
                const prevItems = [...prev]
                  .map((code) => ({
                    ...code,
                    isDeleted: glCdIds.includes(code.id) ? false : code.isDeleted,
                  }))
                  ?.filter((el) => ![...newlyAddedGLCodes, ...deletedGLCodes]?.includes(el?.id));
                return prevItems;
              });
            }
          } else {
            await refreshGLCodePeriodTable();
          }
        } catch (e) {
          console.error(e);
        }
      }
    });
  }, [
    confirm,
    isEditMode,
    isPeriodView,
    refreshGLCodePeriodTable,
    selectedRows,
    setEditingGlCodePeriodBudget,
    setEditingGlCodeYearBudget,
    setGlCodePeriodBudget,
    setGlCodesBudgetByYear,
    setOriginalGlCodePeriodBudget,
    setOriginalGlCodeYearBudget,
    t,
    undeleteGLCode,
  ]);

  return (
    <>
      {undeleteAvailableCategories.includes(
        isPeriodView ? dropdownPeriodFilter.value : yearViewDropdownFilter.value
      ) && (
        <PopUp
          popupAlign={{ horizontal: 'center', vertical: 'top' }}
          content={
            <div className="m-1 max-w-[350px]">
              <PopUpContentWrapper
                type={selectedRows.length > 0 ? 'Error' : 'Information'}
                contents={
                  selectedRows.length > 0
                    ? ['Selected record(s) must be in Deleted state']
                    : ['Please select record(s) to Undelete']
                }
              />
            </div>
          }
          show={isUndeleteDisable}
        >
          <Button onClick={handleUndeleteGLCode} disabled={isUndeleteDisable} color="secondary">
            Undelete
          </Button>
        </PopUp>
      )}
    </>
  );
};

export default UnDeleteGLCode;
